import {
  basisLabel,
  formatQuantity,
  macroEnergySplit,
  type Nutrition,
  perPack,
  type Quantity,
  REFERENCE_KCAL_DAY,
  shareOfReferenceDay,
} from "@larder/shared";

/**
 * What is in it.
 *
 * Led by the WHOLE PACK, because that is the only figure anyone can act on.
 * "415 per 100 g" is a number you compare in a shop; "this packet is 1,240 kcal,
 * two thirds of a day" is a number you make a decision from, and the decision is
 * what this screen is for. The comparable per-100 figure keeps its place as the
 * caption, so nothing is lost — it is demoted, not dropped.
 *
 * The bar is split by ENERGY, not by weight (see `macroEnergySplit`), because a
 * bar drawn by grams would show fat at half its calorific size.
 *
 * These are TYPICAL values recalled by a model, not a reading of the label in
 * front of you. The caption says so plainly rather than in a tooltip, because a
 * number presented without provenance gets trusted like a measurement, and this
 * is not one. See docs/product/nutrition.md.
 */
export function NutritionPanel({
  nutrition,
  size,
}: {
  nutrition: Nutrition;
  size: Quantity | null;
}) {
  const whole = perPack(nutrition, size);
  const share = shareOfReferenceDay(nutrition, size);
  const split = macroEnergySplit(nutrition);

  /**
   * The pack when we can compute it, the basis when we cannot.
   *
   * Never both as the headline. Two big numbers on one card is two cards, and
   * the reader has to work out which one is about the thing in their hand.
   */
  const lead = whole ?? nutrition;
  const leadCaption =
    whole && size
      ? `kcal in ${formatQuantity(size)}`
      : `kcal ${basisLabel(nutrition.basis)}`;

  return (
    <div className="nutrition" data-testid="nutrition">
      <div className="nut-lede">
        <span className="nut-kcal" data-testid="nutrition-kcal">
          {lead.kcal.toLocaleString("en-IN")}
        </span>
        <span className="nut-kcal-label">{leadCaption}</span>
      </div>

      {/* Only when the pack total is real. A share of an unknown pack size would
          be a percentage of nothing, and the reference has to be stated on the
          same line — this app has no idea who is eating it (ADR: nutrition is
          of PURCHASES, never of people). */}
      {share !== null && (
        <div className="nut-share" data-testid="nutrition-share">
          <div className="nut-share-track">
            <div
              className="nut-share-fill"
              /* Capped at the full bar: a 3,000 kcal pack is "a day and a half",
                 and a bar overflowing its track just looks broken. */
              style={{ width: `${Math.min(share, 1) * 100}%` }}
            />
          </div>
          <span className="nut-share-text">
            {Math.round(share * 100)}% of a {REFERENCE_KCAL_DAY.toLocaleString("en-IN")}{" "}
            kcal day
          </span>
        </div>
      )}

      {split && (
        <>
          <div
            className="macro-bar"
            role="img"
            aria-label={`Energy from carbs ${Math.round(split.carbs * 100)}%, fat ${Math.round(split.fat * 100)}%, protein ${Math.round(split.protein * 100)}%`}
            data-testid="macro-bar"
          >
            <span className="seg carbs" style={{ flexGrow: split.carbs }} />
            <span className="seg fat" style={{ flexGrow: split.fat }} />
            <span className="seg protein" style={{ flexGrow: split.protein }} />
          </div>

          {/* The legend doubles as the figures, so the numbers cost no extra
              line. Values are the PACK's when we have one — same subject as the
              headline, or the card would be talking about two things. */}
          <div className="macros">
            <Macro tone="carbs" label="Carbs" value={lead.carbs} />
            <Macro tone="fat" label="Fat" value={lead.fat} />
            <Macro tone="protein" label="Protein" value={lead.protein} />
          </div>
        </>
      )}

      <p className="note subtle" data-testid="nutrition-basis">
        {basisLabel(nutrition.basis)}: {nutrition.kcal} kcal · typical values, not read
        off your pack
      </p>
    </div>
  );
}

function Macro({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "carbs" | "fat" | "protein";
}) {
  return (
    <div className="macro">
      <span className={`macro-dot ${tone}`} aria-hidden="true" />
      <span className="macro-label">{label}</span>
      <span className="macro-value">{value} g</span>
    </div>
  );
}
